import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError, api } from "@/lib/apiClient";
import {
  employeeAccountTypeLabel,
  employeeDisplayFromRecord,
  employeeLoginFromRecord,
  encodeEmployeeLoginParam,
  type HeroEmployeeRecord,
} from "@/lib/employees";
import { formatBudgetDays, listAbsenceBudgets } from "@/lib/employeeAbsences";
import { formatHours, listOvertimeOpeningBalances } from "@/lib/employeeTimes";
import { useCurrentUser } from "@/lib/auth";
import { useRealtimeSync } from "@/lib/useRealtime";
import { usePersonalNavigation } from "@/lib/personalNav";
import {
  userCanManageAbsenceBudgets,
  userCanManageOvertimeOpeningBalances,
} from "@/lib/zeiterfassungPermissions";

interface EmployeeRow extends HeroEmployeeRecord {
  login: string;
  display: string;
  remainingVacation: number | null;
  overtimeBalance: number | null;
}

function heroListToRows(heroList: HeroEmployeeRecord[]): EmployeeRow[] {
  const rows: EmployeeRow[] = [];
  for (const emp of Array.isArray(heroList) ? heroList : []) {
    const login = employeeLoginFromRecord(emp);
    if (!login) continue;
    rows.push({
      ...emp,
      login,
      display: employeeDisplayFromRecord(emp),
      remainingVacation: null,
      overtimeBalance: null,
    });
  }
  rows.sort((a, b) => a.display.localeCompare(b.display, "de"));
  return rows;
}

export function MitarbeiterIndexPage() {
  const { user } = useCurrentUser();
  const { backLink: personalBack } = usePersonalNavigation();
  const canManageBudgets = userCanManageAbsenceBudgets(user);
  const canManageOvertime = userCanManageOvertimeOpeningBalances(user);
  const year = new Date().getFullYear();

  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const loadMetrics = useCallback(async () => {
    if (!canManageBudgets && !canManageOvertime) {
      setMetricsLoading(false);
      return;
    }
    setMetricsLoading(true);
    setMetricsError(null);
    try {
      const [budgets, balances] = await Promise.all([
        canManageBudgets ? listAbsenceBudgets({ year }) : Promise.resolve([]),
        canManageOvertime ? listOvertimeOpeningBalances() : Promise.resolve([]),
      ]);
      const budgetByLogin = new Map(budgets.map((b) => [b.user_login, b]));
      const balanceByLogin = new Map(balances.map((b) => [b.user_login, b]));
      setEmployees((prev) =>
        prev.map((row) => ({
          ...row,
          remainingVacation: budgetByLogin.get(row.login)?.remaining_vacation_days ?? null,
          overtimeBalance: balanceByLogin.get(row.login)?.current_balance_hours ?? null,
        })),
      );
    } catch (err) {
      setMetricsError(
        err instanceof ApiError
          ? err.message
          : "Urlaub/Überstunden konnten nicht geladen werden.",
      );
    } finally {
      setMetricsLoading(false);
    }
  }, [canManageBudgets, canManageOvertime, year]);

  const loadEmployees = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const heroList = await api.get<HeroEmployeeRecord[]>("/hero/employees");
      const rows = heroListToRows(heroList);
      setEmployees((prev) => {
        if (!options?.silent || prev.length === 0) return rows;
        const prevByLogin = new Map(prev.map((row) => [row.login, row]));
        return rows.map((row) => {
          const old = prevByLogin.get(row.login);
          if (!old) return row;
          return {
            ...row,
            remainingVacation: old.remainingVacation,
            overtimeBalance: old.overtimeBalance,
          };
        });
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mitarbeiter konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      await loadEmployees();
      void loadMetrics();
    })();
  }, [loadEmployees, loadMetrics]);

  useRealtimeSync({
    domains: ["employee-times", "hero"],
    onChange: () => {
      void (async () => {
        await loadEmployees({ silent: true });
        void loadMetrics();
      })();
    },
  });

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return employees;
    return employees.filter((row) => {
      const blob = [row.display, row.login, row.email, row.account_type].join(" ").toLowerCase();
      return blob.includes(needle);
    });
  }, [employees, query]);

  return (
    <AppShell
      title="Mitarbeiterverwaltung"
      subtitle="Alle Mitarbeiter im Überblick — Kontingente und Startstände pro Person einstellen."
      back={personalBack ?? { to: "/", label: "Home" }}
    >
      <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
        <label className="block space-y-1">
          <span className="eh-label">Suchen</span>
          <input
            type="search"
            className="eh-input w-full max-w-md"
            placeholder="Name, E-Mail oder Login …"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>

      {error && (
        <Alert variant="error" title="Fehler">
          {error}
        </Alert>
      )}
      {metricsError && (
        <Alert variant="error" title="Kennzahlen">
          {metricsError}
        </Alert>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-ios border border-dashed border-separator bg-card p-8 text-center text-sm text-label-secondary">
          {query ? "Keine Treffer für die Suche." : "Keine Mitarbeiter gefunden."}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-ios border border-separator bg-card shadow-ios">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-separator bg-fill-quaternary/40 text-left text-xs uppercase tracking-wide text-label-tertiary">
                <th className="px-4 py-2">Mitarbeiter</th>
                <th className="px-4 py-2">Konto-Typ</th>
                {canManageBudgets && <th className="px-4 py-2">Urlaub Rest ({year})</th>}
                {canManageOvertime && <th className="px-4 py-2">Überstunden</th>}
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.login} className="border-b border-separator/60 last:border-b-0">
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-label">{row.display}</span>
                      {row.is_nachunternehmer ? (
                        <span className="rounded-full bg-slate-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                          Nachunternehmer
                        </span>
                      ) : null}
                    </div>
                    <div className="text-xs text-label-tertiary">{row.email || row.login}</div>
                  </td>
                  <td className="px-4 py-3 text-label-secondary">
                    {employeeAccountTypeLabel(row.account_type)}
                  </td>
                  {canManageBudgets && (
                    <td className="px-4 py-3 tabular-nums text-label">
                      {metricsLoading && row.remainingVacation == null
                        ? "…"
                        : row.remainingVacation != null
                          ? formatBudgetDays(row.remainingVacation)
                          : "—"}
                    </td>
                  )}
                  {canManageOvertime && (
                    <td className="px-4 py-3 tabular-nums text-label">
                      {metricsLoading && row.overtimeBalance == null
                        ? "…"
                        : row.overtimeBalance != null
                          ? formatHours(row.overtimeBalance)
                          : "—"}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/mitarbeiter/${encodeEmployeeLoginParam(row.login)}`}
                      state={{ employee: row }}
                      className="rounded-full border border-separator px-3 py-1 text-sm font-medium text-label transition hover:border-accent hover:text-accent"
                    >
                      Einstellungen
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
